// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { listMyRooms, listRoomParticipants, getProfilesByIds, getProfilesByEmails, ensureDmRoom } from '@/services/chat';
import { supabase } from '@/integrations/supabase/client';
import { searchUsers, sendFriendRequest } from '@/services/friends';
import ChatWindow from '@/components/dashboard/ChatWindow';
import UserProfilePreviewDialog from '@/components/dashboard/UserProfilePreviewDialog';
import BotEd from '@/components/chat/BotEd';

export default function ChatLauncher() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState<any[]>([]);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [activeTitle, setActiveTitle] = useState<string>('Chat');
  const [previewUserId, setPreviewUserId] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ xPct: number; yPct: number }>({ xPct: 95, yPct: 90 });
  const [activeRoomPeers, setActiveRoomPeers] = useState<any[]>([]);
  const [meProfile, setMeProfile] = useState<any | null>(null);
  const [pinnedOwner, setPinnedOwner] = useState<any | null>(null);
  const [pinnedDev, setPinnedDev] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showBot, setShowBot] = useState(false);

  // Inline search for members to start a new DM
  useEffect(() => {
    let aborted = false;
    const run = async () => {
      try {
        const q = search.trim();
        if (!q) { if (!aborted) setSearchResults([]); return; }
        const results = await searchUsers(q, 25);
        if (!aborted) setSearchResults((results || []).filter((p:any) => p.id !== user?.id));
      } catch {
        if (!aborted) setSearchResults([]);
      }
    };
    run();
    return () => { aborted = true; };
  }, [search, user?.id]);

  useEffect(() => {
    const persisted = localStorage.getItem('chatLauncherEnabled');
    // Default ON unless explicitly disabled
    setEnabled(persisted !== 'false');
  }, []);

  useEffect(() => {
    const syncEnabled = () => {
      const persisted = localStorage.getItem('chatLauncherEnabled');
      setEnabled(persisted === 'true');
    };
    // React to custom events from the Messages tab and storage changes
    const handler = () => syncEnabled();
    window.addEventListener('chatLauncher:enabledChanged', handler as any);
    window.addEventListener('storage', handler as any);
    return () => {
      window.removeEventListener('chatLauncher:enabledChanged', handler as any);
      window.removeEventListener('storage', handler as any);
    };
  }, []);

  // Helper to enrich rooms with display names and previews
  const enrichRooms = async (rs: any[]) => {
    try {
      const me = user?.id;
      const allOtherIds = Array.from(new Set(
        (rs || []).flatMap((r:any) => ((r.participants || []).map((p:any) => p.user_id)).filter((id:string) => id !== me))
      ));
      let profilesMap: Record<string, any> = {};
      if (allOtherIds.length) {
        const profs = await getProfilesByIds(allOtherIds);
        for (const p of profs || []) profilesMap[p.id] = p;
      }
      // Fallback last message previews for rooms missing it (best-effort)
      const missingIds = (rs || []).filter((r:any) => !r.last_message_preview).map((r:any) => r.id);
      let lastByRoom: Record<string, any> = {};
      if (missingIds.length) {
        const { data: lastMsgs } = await supabase
          .from('chat_messages')
          .select('room_id, content, attachment_url, attachment_mime, created_at')
          .in('room_id', missingIds)
          .order('created_at', { ascending: false })
          .limit(200);
        for (const m of (lastMsgs || [])) {
          if (!lastByRoom[m.room_id]) lastByRoom[m.room_id] = m;
        }
      }
      // Compose enriched rooms
      const enriched = (rs || []).map((r:any) => {
        const others = ((r.participants || []).map((p:any) => p.user_id)).filter((id:string) => id !== me);
        let display = r.name || '';
        if (!display) {
          const names = others.map((oid:string) => {
            const p = profilesMap[oid];
            return ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || p?.email || 'User');
          });
          if (r.type === 'dm') display = names[0] || 'Chat';
          else display = names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
          if (!display) display = 'Chat';
        }
        const fallback = lastByRoom[r.id];
        const preview = r.last_message_preview || (fallback ? (fallback.attachment_url ? 'Photo' : (fallback.content || '')) : '');
        const lastAt = r.last_message_at || (fallback?.created_at || null);
        return { ...r, display_name: display, preview, __last_at: lastAt };
      });
      // Sort by last activity desc
      enriched.sort((a:any,b:any) => new Date(b.__last_at || 0).getTime() - new Date(a.__last_at || 0).getTime());
      return enriched;
    } catch {
      return rs || [];
    }
  };

  useEffect(() => {
    if (open) {
      const refreshRooms = async () => {
        try {
          const rs = await listMyRooms();
          const enriched = await enrichRooms(rs);
          setRooms(enriched);
          if (!activeRoomId && enriched && enriched.length) {
            setActiveRoomId(enriched[0].id);
            setActiveTitle(enriched[0].display_name || enriched[0].name || 'Chat');
          }
        } catch {}
      };
      refreshRooms();
      // Realtime refresh on new messages or participant changes
      const channel = supabase
        .channel('chat-launcher-rooms')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_messages' }, () => {
          refreshRooms();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_room_participants' }, () => {
          refreshRooms();
        })
        .subscribe();
      // Load my profile fresh to reflect latest avatar
      getProfilesByIds([user!.id]).then((p) => setMeProfile(p?.[0] || null)).catch(() => {});
      // Load pinned owner and dev
      getProfilesByEmails(['alex@sanctuarywindows.com', 'vaynerov@icloud.com']).then((list) => {
        const owner = (list || []).find((x:any) => (x.email || '').toLowerCase() === 'alex@sanctuarywindows.com');
        const dev = (list || []).find((x:any) => (x.email || '').toLowerCase() === 'vaynerov@icloud.com');
        setPinnedOwner(owner || null);
        setPinnedDev(dev || null);
      }).catch(() => {});
      return () => {
        try { supabase.removeChannel(channel); } catch {}
      };
    }
  }, [open]);

  // Persist last active room and restore on open
  useEffect(() => {
    try {
      if (activeRoomId) localStorage.setItem('chat:lastRoomId', activeRoomId);
    } catch {}
  }, [activeRoomId]);
  useEffect(() => {
    if (open && !activeRoomId) {
      try {
        const last = localStorage.getItem('chat:lastRoomId');
        if (last) setActiveRoomId(last);
      } catch {}
    }
  }, [open]);

  // Conversation list removed; deduplication no longer needed

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If profile preview is open, close it first and keep the chat overlay
        if (previewUserId) {
          e.preventDefault();
          setPreviewUserId(null);
          return;
        }
        if (open) {
          setOpen(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, previewUserId]);

  // Disable body scroll when overlay is open
  useEffect(() => {
    if (open) {
      const prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = prevOverflow; };
    }
    return () => {};
  }, [open]);

  // Compute overlay animation origin from button position
  useEffect(() => {
    const computeAnchor = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const xPct = (cx / window.innerWidth) * 100;
      const yPct = (cy / window.innerHeight) * 100;
      setAnchor({ xPct, yPct });
    };
    computeAnchor();
    window.addEventListener('resize', computeAnchor);
    return () => window.removeEventListener('resize', computeAnchor);
  }, []);

  // Load participants for active room to show contact bar
  useEffect(() => {
    const loadPeers = async () => {
      if (!activeRoomId) { setActiveRoomPeers([]); return; }
      try {
        const ids = await listRoomParticipants(activeRoomId);
        const profiles = await getProfilesByIds(ids);
        setActiveRoomPeers(profiles || []);
      } catch { setActiveRoomPeers([]); }
    };
    loadPeers();
  }, [activeRoomId]);

  if (!user || !enabled) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-overlay"
            className="fixed inset-0 z-[60] bg-slate-950/90 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="h-12 px-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-slate-950/90" onKeyDown={(e) => { if (previewUserId && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPreviewUserId(null); } }}>
              <div className="text-white text-sm">Messages</div>
              <Button size="sm" className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20" onClick={() => setOpen(false)}>Close</Button>
            </div>

            {/* Body */}
            <div className="absolute inset-x-0 bottom-0 top-12 flex">
              <div className="w-64 border-r border-white/10 p-3 flex flex-col">
                <div className="flex-1 overflow-y-auto">
                  <div className="px-1 pb-2">
                    <div className="text-[11px] tracking-wide text-white mb-1">Pinned</div>
                    <div className="space-y-2">
                      {/* Owner first */}
                      {pinnedOwner && (
                        <button className={`w-full px-3 py-3 rounded-xl bg-yellow-500/10 border shadow-[0_0_20px_rgba(212,175,55,0.35)] border-yellow-400/60 flex items-center gap-3 text-left hover:bg-yellow-500/15 transition-colors min-h-[88px]`} onClick={async () => {
                          try {
                            setShowBot(false);
                            const targetId = pinnedOwner.id;
                            if (user?.id && targetId === user.id) { toast({ title: "Can't message yourself" }); return; }
                            const room = await ensureDmRoom(targetId);
                            const displayName = [pinnedOwner.first_name, pinnedOwner.last_name].filter(Boolean).join(' ') || 'Owner';
                            setActiveRoomId(room);
                            setActiveTitle(displayName);
                          } catch (e:any) {
                            toast({ title: 'Unable to open chat', description: 'Try adding as a friend first.', variant: 'destructive' });
                          }
                        }}>
                          <div className="h-12 w-12 shrink-0 rounded-full overflow-hidden border border-yellow-400/60 bg-yellow-500/10 flex items-center justify-center text-sm font-semibold text-yellow-200">
                            {pinnedOwner.avatar_url ? (<img src={pinnedOwner.avatar_url} alt="avatar" className="h-full w-full object-cover object-center" />) : (<span>{`${(pinnedOwner.first_name?.[0]||'')}${(pinnedOwner.last_name?.[0]||'')}`.toUpperCase() || (pinnedOwner.email ? pinnedOwner.email[0].toUpperCase() : 'U')}</span>)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{[pinnedOwner.first_name, pinnedOwner.last_name].filter(Boolean).join(' ') || 'Owner'}</div>
                            <div className="text-[11px] text-white/70 truncate">Owner</div>
                          </div>
                          <div className="ml-auto text-[11px] text-yellow-300">Open</div>
                        </button>
                      )}
                      {/* Dev second */}
                      {pinnedDev && (
                        <button className={`w-full px-3 py-3 rounded-xl bg-red-500/10 border shadow-[0_0_20px_rgba(248,113,113,0.35)] border-red-400/60 flex items-center gap-3 text-left hover:bg-red-500/15 transition-colors min-h-[88px]`} onClick={async () => {
                          try {
                            setShowBot(false);
                            const targetId = pinnedDev.id;
                            if (user?.id && targetId === user.id) { toast({ title: "Can't message yourself" }); return; }
                            const room = await ensureDmRoom(targetId);
                            const displayName = [pinnedDev.first_name, pinnedDev.last_name].filter(Boolean).join(' ') || 'Developer';
                            setActiveRoomId(room);
                            setActiveTitle(displayName);
                          } catch (e:any) {
                            toast({ title: 'Unable to open chat', description: 'Try adding as a friend first.', variant: 'destructive' });
                          }
                        }}>
                          <div className="h-12 w-12 shrink-0 rounded-full overflow-hidden border border-red-400/60 bg-red-500/10 flex items-center justify-center text-sm font-semibold text-red-200">
                            {pinnedDev.avatar_url ? (<img src={pinnedDev.avatar_url} alt="avatar" className="h-full w-full object-cover object-center" />) : (<span>{`${(pinnedDev.first_name?.[0]||'')}${(pinnedDev.last_name?.[0]||'')}`.toUpperCase() || (pinnedDev.email ? pinnedDev.email[0].toUpperCase() : 'U')}</span>)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{[pinnedDev.first_name, pinnedDev.last_name].filter(Boolean).join(' ') || 'Developer'}</div>
                            <div className="text-[11px] text-white/70 truncate">Dev</div>
                          </div>
                          <div className="ml-auto text-[11px] text-red-300">Open</div>
                        </button>
                      )}
                      {/* Bot third */}
                      <button className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3 text-left hover:bg-white/10 transition-colors min-h-[88px]" onClick={() => {
                        setActiveRoomId(null);
                        setActiveTitle('Bot Ed');
                        setPreviewUserId(null);
                        setShowBot(true);
                      }}>
                        <div className="h-12 w-12 shrink-0 rounded-full overflow-hidden border border-emerald-400/40 bg-emerald-600/20 flex items-center justify-center text-sm font-semibold text-emerald-200">BE</div>
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">Bot Ed</div>
                          <div className="text-[11px] text-white/70 truncate">Website assistant</div>
                        </div>
                        <div className="ml-auto text-[11px] text-emerald-300">Open</div>
                      </button>
                    </div>
                    <div className="mt-2 h-px bg-white/10" />
                  </div>
                  {/* Inline search under pinned */}
                  <div className="px-1 pb-2">
                    <input
                      placeholder="Search members or start a chat..."
                      className="w-full rounded-md bg-white/5 border border-white/10 px-2 py-2 text-sm text-white placeholder:text-white/50"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <div className="mt-2 max-h-64 overflow-y-auto space-y-2">
                        {searchResults.map((p:any) => {
                          const displayName = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || 'User';
                          const initials = `${(p.first_name?.[0]||'')}${(p.last_name?.[0]||'')}`.toUpperCase() || (p.email ? p.email[0].toUpperCase() : 'U');
                          return (
                            <div key={p.id} className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                              <div className="h-10 w-10 shrink-0 rounded-full overflow-hidden border border-white/20 bg-white/10 flex items-center justify-center text-xs text-white/80">
                                {p.avatar_url ? (<img src={p.avatar_url} alt="avatar" className="h-full w-full object-cover object-center" />) : (<span>{initials}</span>)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-white truncate">{displayName}</div>
                                <div className="text-[11px] text-white/60 truncate">{p.email}</div>
                              </div>
                              <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400" onClick={async () => {
                                try {
                                  setShowBot(false);
                                  const roomId = await ensureDmRoom(p.id);
                                  setActiveRoomId(roomId);
                                  setActiveTitle(displayName);
                                  setSearch('');
                                  setSearchResults([]);
                                } catch (e:any) {
                                  toast({ title: 'Friends only', description: 'Sending friend request…', variant: 'default' });
                                  try { await sendFriendRequest(p.id); toast({ title: 'Request sent', description: `Friend request sent to ${displayName}` }); } catch {}
                                }
                              }}>Message</Button>
                            </div>
                          );
                        })}
                        {searchResults.length === 0 && (
                          <div className="text-xs text-white/60">No results</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Messages list */}
                  <div className="px-1 pt-2">
                    <div className="text-[11px] tracking-wide text-white/80 mb-1">Messages</div>
                    <div className="space-y-2">
                      {rooms.map((r:any) => (
                        <button key={r.id} className={`w-full text-left px-2 py-2 rounded-lg text-white/80 hover:bg-white/5 flex items-start gap-2 ${activeRoomId === r.id ? 'bg-emerald-500/10 border border-emerald-400/30' : 'border border-transparent'}`} onClick={() => { setShowBot(false); setActiveRoomId(r.id); setActiveTitle(r.display_name || r.name || 'Chat'); }}>
                          <div className="mt-0.5 h-2 w-2 rounded-full bg-emerald-400/60" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-white truncate">{r.display_name || r.name || 'Chat'}</div>
                            {r.preview && (
                              <div className="text-[10px] text-white/50 truncate">{r.preview}</div>
                            )}
                          </div>
                          {r.unread_count > 0 && (
                            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">{r.unread_count > 99 ? '99+' : r.unread_count}</span>
                          )}
                        </button>
                      ))}
                      {rooms.length === 0 && (
                        <div className="text-xs text-white/60 px-1">No conversations yet</div>
                      )}
                    </div>
                  </div>
                  {/* User messages immediately under pinned */}
                  <div className="pt-2" />
                </div>
                {/* Conversation list removed */}
                {user && (
                  <button className="mt-auto w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3 text-left hover:bg-white/10" onClick={() => setPreviewUserId(user.id)}>
                    <div className="h-12 w-12 min-w-[48px] min-h-[48px] shrink-0 rounded-full overflow-hidden border border-white/20 bg-white/10 flex items-center justify-center text-sm font-semibold text-white/80">
                      {meProfile?.avatar_url ? (
                        <img src={`${meProfile.avatar_url}`} alt="me" className="h-full w-full object-cover object-center" />
                      ) : (
                        <span>{`${(meProfile?.first_name?.[0]||'')}${(meProfile?.last_name?.[0]||'')}`.toUpperCase() || (user.email ? user.email[0].toUpperCase() : 'U')}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{[meProfile?.first_name, meProfile?.last_name].filter(Boolean).join(' ') || 'Me'}</div>
                      <div className="text-[11px] text-white/60 truncate">{user.email}</div>
                    </div>
                    <div className="ml-auto text-[11px] text-emerald-300">Profile</div>
                  </button>
                )}
              </div>
              <div className="flex-1 p-3">
                {showBot ? (
                  <div className="h-full"><BotEd onClose={() => setShowBot(false)} /></div>
                ) : activeRoomId ? (
                  <div className="h-full"><ChatWindow roomId={activeRoomId} title={activeTitle} onOpenProfile={(id) => setPreviewUserId(id)} /></div>
                ) : (
                  <div className="h-full flex items-center justify-center text-white/60 text-sm">Select a conversation</div>
                )}
              </div>
            </div>

            <UserProfilePreviewDialog open={!!previewUserId} onOpenChange={(v) => !v && setPreviewUserId(null)} userId={previewUserId} />
          </motion.div>
        )}
      </AnimatePresence>
      <Button ref={btnRef as any} className={`h-14 w-14 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white ring-2 ${open ? 'ring-white shadow-[0_0_60px_rgba(255,255,255,1),0_0_140px_rgba(16,185,129,0.7),0_0_220px_rgba(20,184,166,0.6)]' : 'ring-white/80 shadow-[0_0_30px_rgba(255,255,255,0.9),0_0_60px_rgba(255,255,255,0.6),0_0_90px_rgba(255,255,255,0.3)]'}`} onClick={() => {
        // refresh anchor on open for accurate animation
        if (!open) {
          const rect = btnRef.current?.getBoundingClientRect();
          if (rect) {
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            setAnchor({ xPct: (cx / window.innerWidth) * 100, yPct: (cy / window.innerHeight) * 100 });
          }
        }
        setOpen((v) => !v);
      }}>
        <MessageSquare className="h-5 w-5" />
      </Button>
    </div>
  );
}


